import {
	HttpClient,
	HttpResponse
}                                    from '@angular/common/http';
import { Injectable }                from '@angular/core';
import {
	Tenant,
	TenantForm,
	TenantInitialization
}                                    from '@app/modules/admin-tenant/models/tenant.model';
import {
	ElsterConfig,
	EricConfig,
	SystemConfig
}                                    from '@app/modules/configuration/models/configuration.model';
import {
	IAdminTenantHttpParams,
	IBaseHttpParams,
	RequestActionEnum,
	RequestCompleteEntityState,
	RequestCompleteListState,
	RequestState
}                                    from '@core/interfaces/api.interface';
import { AbstractApiService }        from '@http/abstract-api.service';
import { TenantApiSanitizerService } from '@http/admin/tenant-api-sanitizer.service';
import { TransitionService }         from '@http/transition.service';
import {
	AdminInitializeApiClient,
	AdminSystemConfigurationBodyDTO,
	AdminTenantsApiClient,
	ClsInitializationDTO,
	ClsTenantBodyDTO,
	ClsTenantStatusDTO,
	ConfigTypeDTO,
	CreateTenantRequestParams,
	ElsterConfigurationDTO,
	GetTenantElsterConfigRequestParams as AdminGetTenantElsterConfigRequestParams,
	GetTenantRequestParams,
	GetTenantsRequestParams,
	GetTenantsWithInvalidCustomerRequestParams,
	GetTenantTenantsRequestParams,
	InitializationDTO,
	InitializeRequestParams,
	TenantBodyDTO,
	TenantDTO,
	TenantGroupEnumDTO,
	TenantsResponseDTO,
	TenantStatusDTO,
	UpdateTenantElsterConfigRequestParams as AdminUpdateTenantElsterConfigRequestParams,
	UpdateTenantRequestParams,
	UpdateTenantStatusRequestParams,
	UpdateTenantSystemConfigRequestParams
}                                    from '@openApiGenerated/admin-api';
import {
	AdminTenantsApiClient as AdminElsterTenantsApiClient,
	EricConfigurationDTO,
	GetTenantEricConfigRequestParams as AdminGetTenantEricConfigRequestParams,
	UpdateTenantEricConfigRequestParams as AdminUpdateTenantEricConfigRequestParams
}                                    from '@openApiGenerated/eric-wrapper-admin-api';
import { SystemConfigurationDTO }    from '@openApiGenerated/management-api';
import { LoggerService }             from '@shared/services/logger.service';
import {
	Observable,
	of
}                                    from 'rxjs';

@Injectable( {
	providedIn: 'root'
} )
export class AdminTenantApiService extends AbstractApiService {

	constructor(
		protected http: HttpClient,
		protected logger: LoggerService,
		private readonly _adminElsterTenantsApiClient: AdminElsterTenantsApiClient,
		private readonly _adminInitializeApiClient: AdminInitializeApiClient,
		private readonly _adminTenantsApiClient: AdminTenantsApiClient,
		private readonly _transition: TransitionService,
		private readonly _tenantApiSanitizerService: TenantApiSanitizerService
	) {
		super();
		this.http = http;
	}

	// SANITIZER ######################################
	get sanitizer(): TenantApiSanitizerService {
		return this._tenantApiSanitizerService;
	}

	// LIST TYPE REQUESTS #############################
	public search( params: IAdminTenantHttpParams, throwError = false, tenantId?: string ): Observable<RequestCompleteListState<Tenant>> {
		this.logger.trace( 'pre-search call: ', URL );

		let req$;
		if ( tenantId ) {
			// searching for Tenants subaccounts
			const requestParamTenant: GetTenantTenantsRequestParams = {
				tenantId,
				pageNumber: params?.paging?.pageNumber,
				pageSize: params?.paging?.pageSize,
				sort: params?.sort
			};
			req$ = this._adminTenantsApiClient.getTenantTenants( requestParamTenant ) as any;
		} else {
			// searching for Tenants
			const requestParam: GetTenantsRequestParams = {
				archivingContractId: params?.archivingContractId,
				contractMonth: params?.contractMonth,
				customerIdentifier: params?.customerIdentifier,
				elsterContractId: params?.elsterContractId,
				filter: params?.filter,
				fiscalContractId: params?.fiscalContractId,
				pageNumber: params?.paging?.pageNumber,
				pageSize: params?.paging?.pageSize,
				requiredTenantId: params?.requiredTenantId,
				search: this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.search ),
				sort: params?.sort,
				status: this.arrayUtils.checkForSingleValueArgAndReturnAsArray( params?.status ),
				type: params.type,
				tenantGroup: params.tenantGroup as unknown as TenantGroupEnumDTO[],
				tenantIds: params?.tenantIds
			};

			// searching for tenants parentTenants
			if ( requestParam.tenantIds?.length ) {
				delete requestParam.filter;
				delete requestParam.search;
				delete requestParam.sort;
				delete requestParam.status;
			}

			req$ = this._adminTenantsApiClient.getTenants( requestParam ) as any;
		}
		return this._transition.handleListRequestResponse<TenantsResponseDTO, Tenant>(
			req$,
			Tenant,
			this,
			this.sanitizer.sanitizeApiResponse,
			null,
			null,
			throwError
		);
	}

	public list(): Observable<RequestCompleteListState<Tenant>> {
		this.logger.trace( 'pre-list call' );
		const requestParam: GetTenantsRequestParams = {
			filter: null,
			pageNumber: null,
			pageSize: null,
			search: null,
			sort: 'lastModifiedDate,desc',
			status: null,
			tenantGroup: null,
			type: null
		};

		const req$ = this._adminTenantsApiClient.getTenants( requestParam ) as any;
		return this._transition.handleListRequestResponse<TenantsResponseDTO, Tenant>(
			req$, Tenant, this, this.sanitizer.sanitizeApiResponse
		);
	}

	public listTenantsWithInvalidCustomer( params: IBaseHttpParams ): Observable<RequestCompleteListState<Tenant>> {
		this.logger.trace( 'pre-listTenantsWithInvalidCustomer call' );
		const requestParam: GetTenantsWithInvalidCustomerRequestParams = {
			pageNumber: params?.paging?.pageNumber ?? null,
			pageSize: params?.paging?.pageSize ?? null,
			sort: params?.sort ?? 'customerIdentifier,asc'
		};

		const req$ = this._adminTenantsApiClient.getTenantsWithInvalidCustomer( requestParam ) as any;
		return this._transition.handleListRequestResponse<TenantsResponseDTO, Tenant>(
			req$, Tenant, this, this.sanitizer.sanitizeApiResponse
		);
	}

	// ENTITY TYPE REQUESTS ###########################
	public initialize( tenantInitialization: TenantInitialization, throwError = false ): Observable<RequestCompleteEntityState<Tenant>> {
		this.logger.trace( 'pre-initialize call: ', { ...tenantInitialization } );
		const initializationDTO = this._transition.prepareEntityRequest<TenantInitialization, InitializationDTO>(
			tenantInitialization, ClsInitializationDTO, this, this.sanitizer.sanitizeForAdminApiRequest
		);
		const requestParam: InitializeRequestParams = {
			initializationDTO
		};

		const req$ = this._adminInitializeApiClient.initialize( requestParam );
		return this._transition.handleEntityRequestResponse<TenantDTO | HttpResponse<TenantDTO>, Tenant>(
			req$,
			RequestActionEnum.CREATE,
			Tenant,
			this,
			null,
			null,
			null,
			null,
			throwError
		);
	}

	public create( tenantForm: TenantForm, throwError = false ): Observable<RequestCompleteEntityState<Tenant>> {
		this.logger.trace( 'pre-create call: ', { ...tenantForm } );
		const tenantBodyDTO = this._transition.prepareEntityRequest<TenantForm, TenantBodyDTO>(
			tenantForm, ClsTenantBodyDTO, this, this.sanitizer.sanitizeForAdminApiRequest
		);
		const requestParam: CreateTenantRequestParams = {
			tenantBodyDTO
		};

		const req$ = this._adminTenantsApiClient.createTenant( requestParam );
		return this._transition.handleEntityRequestResponse<TenantDTO | HttpResponse<TenantDTO>, Tenant>(
			req$,
			RequestActionEnum.CREATE,
			Tenant,
			this,
			null,
			null,
			null,
			null,
			throwError
		);
	}

	public read( id: string, withFullResponse?: boolean, throwError = false ): Observable<RequestCompleteEntityState<Tenant>> {
		this.logger.trace( 'pre-read call: ', id );
		const requestParam: GetTenantRequestParams = {
			tenantId: id
		};

		const req$ = this._adminTenantsApiClient.getTenant( requestParam, withFullResponse ? 'response' : undefined );
		return this._transition.handleEntityRequestResponse<TenantDTO | HttpResponse<TenantDTO>, Tenant>(
			req$,
			RequestActionEnum.READ,
			Tenant,
			this,
			this.sanitizer.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public update( tenantForm: TenantForm, throwError = false ): Observable<RequestCompleteEntityState<Tenant>> {
		this.logger.trace( 'pre-update call: ', { ...tenantForm } );
		const tenantBodyDTO = this._transition.prepareEntityRequest<TenantForm, TenantBodyDTO>(
			tenantForm, ClsTenantBodyDTO, this, this.sanitizer.sanitizeForAdminApiRequest
		);
		const requestParam: UpdateTenantRequestParams = {
			ifMatch: tenantForm.etag,
			tenantBodyDTO,
			tenantId: tenantForm.id
		};

		const req$ = this._adminTenantsApiClient.updateTenant( requestParam, 'response' );
		return this._transition.handleEntityRequestResponse<TenantDTO | HttpResponse<TenantDTO>, Tenant>(
			req$,
			RequestActionEnum.UPDATE,
			Tenant,
			this,
			this.sanitizer.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public changeStatus( tenant: Tenant, throwError = false ): Observable<RequestCompleteEntityState<Tenant>> {
		this.logger.trace( 'pre-update call: ', { ...tenant } );
		const tenantStatusDTO = this._transition.prepareEntityRequest<Tenant, TenantStatusDTO>(
			tenant, ClsTenantStatusDTO, this
		);
		const requestParam: UpdateTenantStatusRequestParams = {
			ifMatch: tenant.etag,
			tenantId: tenant.id,
			tenantStatusDTO
		};

		const req$ = this._adminTenantsApiClient.updateTenantStatus( requestParam );
		return this._transition.handleEntityRequestResponse<TenantDTO, Tenant>(
			req$,
			RequestActionEnum.UPDATE,
			Tenant,
			this,
			this.sanitizer.sanitizeApiResponse,
			null,
			null,
			null,
			throwError
		);
	}

	public updateSystemConfig(
		options: {
			configuration: SystemConfig,
			tenantId: string,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<SystemConfig>> {
		const {
			configuration,
			tenantId,
			withFullResponse = true
		} = options;

		this.logger.debug( 'pre-updateTenantConfig call:', configuration, tenantId );

		const systemConfigurationBodyDTO: AdminSystemConfigurationBodyDTO = {
			archiving: {
				enabled: configuration?.archiving?.enabled,
				ersCheck: configuration?.archiving?.ersCheck,
				strictReferenceCheck: configuration?.archiving?.strictReferenceCheck
			},
			type: configuration?.type as unknown as ConfigTypeDTO
		};

		const requestParam: UpdateTenantSystemConfigRequestParams = {
			adminSystemConfigurationBodyDTO: systemConfigurationBodyDTO,
			ifMatch: configuration?.archivingConfigs?.systemConfigEtag ?? '-1',
			tenantId
		};

		const req$ = this._adminTenantsApiClient.updateTenantSystemConfig( requestParam, withFullResponse ? 'response' : undefined );
		return this._transition.handleEntityRequestResponse<SystemConfigurationDTO | HttpResponse<SystemConfigurationDTO>, SystemConfig>(
			req$,
			RequestActionEnum.UPDATE,
			SystemConfig,
			this,
			this.sanitizer.sanitizeSystemConfigApiResponse
		);
	}

	public readElsterConfig(
		options: {
			tenantId: string,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<ElsterConfig>> {
		const {
			tenantId,
			withFullResponse = true
		} = options;

		this.logger.debug( 'pre-readElsterConfig call:' );

		const requestParam: AdminGetTenantElsterConfigRequestParams = {
			tenantId
		};

		const req$ = this._adminTenantsApiClient.getTenantElsterConfig( requestParam, withFullResponse ? 'response' : undefined );

		return this._transition.handleEntityRequestResponse<ElsterConfigurationDTO | HttpResponse<ElsterConfigurationDTO>, ElsterConfig>(
			req$,
			RequestActionEnum.READ,
			ElsterConfig,
			this,
			this.sanitizer.sanitizeElsterConfigApiResponse
		);
	}

	public updateElsterConfig(
		options: {
			configuration: ElsterConfig
			tenantId: string,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<ElsterConfig>> {
		const {
			configuration,
			tenantId,
			withFullResponse = true
		} = options;

		this.logger.debug( 'pre-updateElsterConfig call:', configuration, tenantId );

		const elsterConfigurationDTO: ElsterConfigurationDTO = {
			ericEnabled: configuration.taxationReportingEnabled,
			type: configuration?.type as unknown as ConfigTypeDTO
		};

		const requestParam: AdminUpdateTenantElsterConfigRequestParams = {
			elsterConfigurationDTO,
			ifMatch: configuration?.etag ?? '-1',
			tenantId
		};

		const req$ = this._adminTenantsApiClient.updateTenantElsterConfig( requestParam, withFullResponse ? 'response' : undefined );
		return this._transition.handleEntityRequestResponse<ElsterConfigurationDTO | HttpResponse<ElsterConfigurationDTO>, ElsterConfig>(
			req$,
			RequestActionEnum.UPDATE,
			ElsterConfig,
			this,
			this.sanitizer.sanitizeElsterConfigApiResponse
		);
	}

	public readEricConfig(
		options: {
			tenantId: string,
			useAdminApi?: boolean,
			withFullResponse?: boolean
		} ): Observable<RequestCompleteEntityState<EricConfig>> {
		const {
			tenantId,
			useAdminApi,
			withFullResponse = true
		} = options;

		const requestAction = RequestActionEnum.READ;

		this.logger.debug( 'pre-readEricConfig call:' );

		if ( useAdminApi ) {
			const requestParam: AdminGetTenantEricConfigRequestParams = {
				tenantId
			};

			const req$ = this._adminElsterTenantsApiClient.getTenantEricConfig( requestParam, withFullResponse ? 'response' : undefined );
			return this._transition.handleEntityRequestResponse<ElsterConfigurationDTO | HttpResponse<EricConfigurationDTO>, EricConfig>(
				req$,
				requestAction,
				EricConfig,
				this
			);
		}

		return of( this._transition.getEmptyRequestCompleteEntityState<EricConfig>( requestAction, RequestState.NOT_SUPPORTED ) );
	}

	public updateEricConfig(
		options: {
			tenantId: string,
			configuration: EricConfig,
			useAdminApi?: boolean,
			withFullResponse?: boolean
		} ): Observable<RequestCompleteEntityState<EricConfig>> {
		const {
			configuration,
			tenantId,
			useAdminApi,
			withFullResponse = true
		} = options;

		const requestAction = RequestActionEnum.UPDATE;

		this.logger.debug( 'pre-updateEricConfig call:', configuration, tenantId );

		if ( useAdminApi ) {
			const ericConfigurationDTO: EricConfigurationDTO = {
				disabledExpiration: configuration.disabledExpiration
			};

			const requestParam: AdminUpdateTenantEricConfigRequestParams = {
				ericConfigurationDTO,
				ifMatch: configuration?.etag ?? '-1',
				tenantId
			};

			const req$ = this._adminElsterTenantsApiClient.updateTenantEricConfig( requestParam, withFullResponse ? 'response' : undefined );
			return this._transition.handleEntityRequestResponse<EricConfigurationDTO | HttpResponse<EricConfigurationDTO>, EricConfig>(
				req$,
				requestAction,
				EricConfig,
				this
			);
		}

		return of( this._transition.getEmptyRequestCompleteEntityState<EricConfig>( requestAction, RequestState.NOT_SUPPORTED ) );
	}
}
